import React from 'react';
import PropTypes from 'prop-types';

import Button from '../../library/button/button';
import Typography from '../../library/typography/typography';
import MinimumPriceMessage from '../../components/minimum-price-message/minimum-price-message';
import InstallationCostsMessage from '../installation-costs-message/installation-costs-message';
import MarkdownPrice from '../markdown-price/markdown-price';
import Price from '../price/price';
import ProductDescription from '../product-description/product-description';
import ProductImage from '../product-image/product-image';
import ProductOECompatible from '../product-oe-compatible/product-oe-compatible';
import ProductSwatchDescription from '../product-swatch-description/product-swatch-description';
import QuantityDisclaimer from '../quantity-disclaimer/quantity-disclaimer';
import StaticQuantity from '../static-quantity/static-quantity';
import ViewOnVehicleButton from '../view-on-vehicle-button/view-on-vehicle-button';

import {
  useMyAccount,
  useProductEntryDataLayerValues
} from '../../shared/hooks/hooks';

import { trackAction } from '../../shared/utils/analytics/analytics';
import {
  getNormalizedProductType,
  getPriceInfo,
  getShouldShowMinimumPriceMessage
} from '../../shared/utils/product/product';
import { ProductProp } from '../../shared/utils/prop-types/prop-types';

import { PRODUCT_TYPES } from '../../shared/constants/products';

import './bundle-item-product-description.scss';

function BundleItemProductDescription(props) {
  const {
    axleType,
    entry,
    hideBrand = false,
    hideChangeProduct,
    hideGbb = false,
    hideImage = false,
    hideLabel = false,
    hideLogo = false,
    hideName = false,
    hideQty = false,
    isPackage,
    isPrebuilt,
    isPriceForEach = false,
    isStaggered,
    link,
    onChangeProduct,
    shouldDisplayTotalPrice,
    shouldShowCartPrice = false,
    showInstallationCostsMessage = false,
    showMinimumPriceMessage: showMinimumPriceMessageProp = false,
    showProductCode = false,
    showQuantityDisclaimer = false,
    showViewOnVehicle = false
  } = props;
  const {
    basePrice,
    mapPrice: entryMapPrice,
    markdownPriceData: entryMarkdownPriceData,
    product,
    quantity
  } = entry || {};
  const { vendorFinish } = product || {};
  const { isFleetAccount } = useMyAccount();
  const { markdownPriceData: productMarkdownPriceData } = product || {};
  const { mapPrice: productMapPrice, price: productPrice } =
    getPriceInfo(product);
  const isFleetAccountEnabled =
    process.env.__FF_FLEET_ACCOUNT__ === 'true' && isFleetAccount;

  const {
    formattedPercentageSaved: productFormattedPercentageSaved,
    markDownPriceType: productMarkDownPriceType,
    originalRetailValue: productOriginalRetailValue
  } = productMarkdownPriceData ?? {};

  const {
    formattedPercentageSaved: entryFormattedPercentageSaved,
    markDownPriceType: entryMarkDownPriceType,
    originalRetailValue: entryOriginalRetailValue
  } = entryMarkdownPriceData ?? {};

  const markDownPriceType = isPrebuilt
    ? productMarkDownPriceType
    : entryMarkDownPriceType;

  const originalPrice =
    isPrebuilt && productOriginalRetailValue
      ? productOriginalRetailValue * quantity
      : entryOriginalRetailValue;

  const percentageSaved = isPrebuilt
    ? productFormattedPercentageSaved
    : entryFormattedPercentageSaved;

  //Added this logic for B2B because fleet price is available at the entry level and not at product level
  let price = 0;
  if (isFleetAccountEnabled) {
    price = basePrice || productPrice;
  } else {
    price = shouldShowCartPrice
      ? entryMapPrice?.value || productPrice
      : productPrice;
  }

  const mapPrice = shouldShowCartPrice
    ? {}
    : {
        ...productMapPrice,
        price:
          productMapPrice?.price *
          (isPriceForEach && !shouldDisplayTotalPrice ? 1 : quantity)
      };

  const showMinimumPriceMessage =
    showMinimumPriceMessageProp && getShouldShowMinimumPriceMessage(product);
  const productType = getNormalizedProductType(product);
  const logoUrl = entry?.product?.brandLogo?.url;

  const { getProductEntryDataLayerValues } = useProductEntryDataLayerValues();

  return (
    <div
      className="bundle-item-product-description"
      styleName={`container ${
        isStaggered || !isPackage || product?.isOtherVehiclesOE
          ? 'container--without-border'
          : ''
      }`}
    >
      {!hideLabel && (
        <Typography
          className="bundle-item-product-description__type"
          styleName="type"
          tag="div"
          variant="H5"
          weight="black"
        >
          {productType}
        </Typography>
      )}
      <div styleName="image-product-container">
        {!hideImage && (
          <ProductImage
            height={114}
            product={entry?.product}
            styleName="image"
            width={114}
          />
        )}
        <div
          className="bundle-item-product-description__product-container"
          styleName="product-container"
        >
          <div styleName="description-section">
            <div>
              <div>
                {isStaggered && (
                  <Typography tag="span" weight="black">
                    {axleType}
                  </Typography>
                )}
                <ProductDescription
                  brand={!hideBrand && product?.brand}
                  brandLogoUrl={!hideLogo && logoUrl}
                  code={showProductCode && product?.code}
                  gbb={!hideGbb && product?.gbb}
                  link={link}
                  name={!hideName && product?.name}
                  showGBBTooltip={!hideGbb}
                  size={product?.size}
                />
                {showViewOnVehicle &&
                  isPrebuilt &&
                  productType === PRODUCT_TYPES.WHEELS && (
                    <div styleName="vov">
                      <ViewOnVehicleButton
                        detailUrl={link}
                        hideAddProduct
                        hideFilters
                        onClick={() =>
                          trackAction(
                            'product_list_view_on_vehicle',
                            getProductEntryDataLayerValues(product)
                          )
                        }
                        product={product}
                        shouldSkipQuery
                      />
                    </div>
                  )}
              </div>
              {showViewOnVehicle && !isPrebuilt && (
                <ViewOnVehicleButton
                  detailUrl={link}
                  hideAddProduct
                  onClick={() =>
                    trackAction(
                      'product_list_view_on_vehicle',
                      getProductEntryDataLayerValues(product)
                    )
                  }
                  product={product}
                  shouldSkipQuery
                />
              )}
              {vendorFinish &&
                productType === PRODUCT_TYPES.WHEELS &&
                !isStaggered && (
                  <div styleName="swatch-description">
                    <ProductSwatchDescription finish={vendorFinish} />
                  </div>
                )}

              <div
                className="bundle-item-product-description__price-qty-container"
                styleName="price-qty-container"
              >
                {price && (
                  <div styleName="price-container">
                    {isFleetAccountEnabled && (
                      <MarkdownPrice
                        markDownPriceType={markDownPriceType}
                        originalPrice={originalPrice}
                        percentageSaved={percentageSaved}
                      />
                    )}
                    <Price
                      isPriceForEach={isPriceForEach}
                      mapPrice={mapPrice}
                      price={price * (isPriceForEach ? 1 : quantity)}
                      unitName={product?.unitName || 'EA'}
                      variant="H3"
                    />
                    {showMinimumPriceMessage && (
                      <MinimumPriceMessage
                        message={product?.mapDisplayRule?.message}
                      />
                    )}
                    {showInstallationCostsMessage && (
                      <InstallationCostsMessage />
                    )}
                  </div>
                )}
                {!isStaggered && !hideQty && (
                  <StaticQuantity isHorizontal quantity={quantity} />
                )}
              </div>
            </div>
            {isStaggered && !hideQty && (
              <StaticQuantity
                quantity={quantity}
                styleName="staggered-quantity"
              />
            )}
          </div>
          {showQuantityDisclaimer && (
            <QuantityDisclaimer
              productType={productType}
              quantity={quantity}
              styleName="quantity-disclaimer"
            />
          )}

          <div
            className="bundle-item-product-description__ctas"
            styleName="ctas"
          >
            {onChangeProduct && !hideChangeProduct && (
              <Button onClick={onChangeProduct}>
                change {productType?.toLowerCase()}
              </Button>
            )}
          </div>
        </div>
      </div>
      {!isStaggered && product?.isOtherVehiclesOE && (
        <ProductOECompatible styleName="product-oe-compatible" />
      )}
    </div>
  );
}

BundleItemProductDescription.propTypes = {
  axleType: PropTypes.string,
  entry: PropTypes.shape({
    entryNumber: PropTypes.number,
    product: ProductProp,
    quantity: PropTypes.number
  }),
  hideBrand: PropTypes.bool,
  hideChangeProduct: PropTypes.bool,
  hideGbb: PropTypes.bool,
  hideImage: PropTypes.bool,
  hideLabel: PropTypes.bool,
  hideLogo: PropTypes.bool,
  hideName: PropTypes.bool,
  hideQty: PropTypes.bool,
  isPackage: PropTypes.bool,
  isPrebuilt: PropTypes.bool,
  isPriceForEach: PropTypes.bool,
  isStaggered: PropTypes.bool,
  link: PropTypes.string,
  onChangeProduct: PropTypes.func,
  shouldDisplayTotalPrice: PropTypes.bool,
  shouldShowCartPrice: PropTypes.bool,
  showInstallationCostsMessage: PropTypes.bool,
  showMinimumPriceMessage: PropTypes.bool,
  showProductCode: PropTypes.bool,
  showQuantityDisclaimer: PropTypes.bool,
  showViewOnVehicle: PropTypes.bool
};

export default BundleItemProductDescription;
